import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilePlus2, Loader2, Store } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/base/command";
import { useStateMachine } from "@/core/arch/state_machine";
import { useHasGroup } from "@/hooks/useHasGroup";
import {
  SEARCH_DEBOUNCE_MS,
  useDebouncedValue,
} from "@/hooks/useDebouncedValue";
import { ROUTES } from "@/routes";
import { UniversalSearchBloc } from "@/application/search/universal_search_bloc";
import { searchIndexRepository } from "@/application/search/search_index_repository";
import { AmplifySearchRepository } from "@/application/search/search_repository";
import {
  allNavItems,
  visibleNavItems,
  type NavItem,
} from "@/presentation/layout/nav_items";

interface PaletteAction {
  label: string;
  to: string;
  icon: typeof FilePlus2;
  requiresGroup?: string;
}

/**
 * Deliberately excludes destinations the sidebar already offers — the nav
 * registry supplies those, and listing one in both groups renders it twice.
 */
const ACTIONS: PaletteAction[] = [
  {
    label: "Create merchant",
    to: ROUTES.CREATE_MERCHANT,
    icon: FilePlus2,
    requiresGroup: "CPOS-Developers",
  },
];

function matchesLabel(label: string, query: string): boolean {
  return label.toLowerCase().includes(query.trim().toLowerCase());
}

/**
 * Global search, opened with ⌘K.
 *
 * Result order is fixed rather than interleaved — exact lookups, then index
 * matches, then destinations, then actions — so the same keystrokes always
 * land on the same row.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const isDeveloper = useHasGroup("CPOS-Developers");
  const [input, setInput] = useState("");

  const [state, bloc] = useStateMachine(
    () =>
      new UniversalSearchBloc(
        searchIndexRepository,
        new AmplifySearchRepository(),
      ),
  );

  const debouncedInput = useDebouncedValue(input, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    void bloc.setQuery(debouncedInput);
  }, [debouncedInput, bloc]);

  // Reopening should not show the previous search's results.
  useEffect(() => {
    if (!open) setInput("");
  }, [open]);

  const groups = useMemo(
    () => (isDeveloper ? ["CPOS-Developers"] : []),
    [isDeveloper],
  );

  const navMatches: NavItem[] = useMemo(() => {
    if (input.trim() === "") return [];
    return visibleNavItems(allNavItems(), groups).filter((item) =>
      matchesLabel(item.label, input),
    );
  }, [input, groups]);

  const actionMatches = useMemo(() => {
    if (input.trim() === "") return [];
    return ACTIONS.filter(
      (action) =>
        (!action.requiresGroup || groups.includes(action.requiresGroup)) &&
        matchesLabel(action.label, input),
    );
  }, [input, groups]);

  function go(path: string) {
    onOpenChange(false);
    navigate(path);
  }

  // Covers both halves of the wait: the debounce gap, and the live lookups
  // that run after it.
  const searching = state.status === "searching" || debouncedInput !== input;
  const hasResults =
    state.exact.length > 0 ||
    state.merchants.length > 0 ||
    navMatches.length > 0 ||
    actionMatches.length > 0;

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      shouldFilter={false}
      title="Search"
      description="Search merchants, identifiers, pages and actions"
    >
      <CommandInput
        placeholder="Search merchants, an email, a Stripe or record ID…"
        value={input}
        onValueChange={setInput}
        trailing={
          searching ? (
            <Loader2
              aria-label="Searching"
              className="text-muted-foreground size-4 shrink-0 animate-spin"
            />
          ) : null
        }
      />
      <CommandList>
        {!hasResults && (
          <CommandEmpty>
            {searching ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching…
              </span>
            ) : input.trim() === "" ? (
              "Search by business name, owner, email, or paste an ID."
            ) : state.failed ? (
              "Search is unavailable right now — try again in a moment."
            ) : (
              "No matches."
            )}
          </CommandEmpty>
        )}

        {state.exact.length > 0 && (
          <CommandGroup heading="Exact match">
            {state.exact.map((item) => (
              <CommandItem
                key={`exact-${item.id}`}
                value={`exact-${item.id}`}
                onSelect={() => go(item.path)}
              >
                <Store />
                <span className="flex-1">{item.title}</span>
                {item.subtitle && (
                  <span className="text-muted-foreground truncate text-xs">
                    {item.subtitle}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {state.merchants.length > 0 && (
          <CommandGroup heading="Merchants">
            {state.merchants.map((item) => (
              <CommandItem
                key={`merchant-${item.id}`}
                value={`merchant-${item.id}`}
                onSelect={() => go(item.path)}
              >
                <Store />
                <span className="flex-1">{item.title}</span>
                {item.subtitle && (
                  <span className="text-muted-foreground truncate text-xs">
                    {item.subtitle}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {navMatches.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Go to">
              {navMatches.map((item) => (
                <CommandItem
                  key={`nav-${item.to}`}
                  value={`nav-${item.to}`}
                  onSelect={() => go(item.to)}
                >
                  <item.icon />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {actionMatches.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Actions">
              {actionMatches.map((action) => (
                <CommandItem
                  key={`action-${action.to}`}
                  value={`action-${action.to}`}
                  onSelect={() => go(action.to)}
                >
                  <action.icon />
                  {action.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
